const ChromeOSPwaExtensionMessaging = require("./ChromeOSPwaExtensionMessaging");
const RebootScheduler = require("./RebootScheduler");
const ConfigurationFactory = require("../ConfigurationFactory");
const ContentSentinelDispatcher = require("content-sentinel");

const DisplayControlConfigurationLogging = require("../Display/DisplayControlConfigurationLogging");
const DisplayControlHandler = require("../Display/DisplayControlHandler");
const VolumeControlService = require("../Display/VolumeControlService");
const Logger = require("../Logger");
const Utils = require("../Utils");
const MachineId = require("../Machine-id");
const RemoteCacheController = require("./RemoteCacheController");

const service = {
  lastExitError: null,
  lastExitErrorTimestamp: null,
  displayOff: null
};

const ONE_MINUTE = 60000;

service.getInjectedPlayer = () => {
  if (typeof AndroidPlayer !== 'undefined') {
    return AndroidPlayer;
  } else if (window.RisePlayer) {
    return window.RisePlayer;
  }

  // This is the case for webplayer, no injected player object there.
  Logger.viewerDebug( "Injected player object is not available" );

  return null
}

function screenPowerStateFromScreenReading(screenOff, displayControlOff) {
  return !screenOff ? "ON" : displayControlOff ? "OFF - EXPECTED" : "OFF - UNEXPECTED";
}

service.notifyPowerStatusUpdate = update => {
  const screenStatusOff = update && typeof update.screenOff === "boolean" ? update.screenOff : null;

  const isScreenStatusUnknown = update && typeof update.isCecUnknown === "boolean" ? update.isCecUnknown : null;

  if (screenStatusOff === null && !isScreenStatusUnknown) {
    Logger.viewerWarning("Invalid screen power update payload", update);
    return;
  }

  const dcStatusOff = service.displayOff;

  let state;
  if (isScreenStatusUnknown) {
    Logger.viewerWarning("HDMI CEC state unknown; check CEC cable or restart the device.", update);
    state = "UNKNOWN"
  } else {
    state = screenPowerStateFromScreenReading(screenStatusOff, dcStatusOff);
  }
  service.sendScreenPowerState(state);
}

service.subscribeToScreenPowerUpdates = () => {
  const injectedPlayer = service.getInjectedPlayer();

  if (!injectedPlayer || typeof injectedPlayer.subscribeToScreenPowerStatusUpdates !== "function") { return; }

  if (ConfigurationFactory.isWebOSPlayer()) {
    injectedPlayer.subscribeToScreenPowerStatusUpdates( service.notifyPowerStatusUpdate );
  } else if (ConfigurationFactory.isAndroidPlayer()) {
    injectedPlayer.subscribeToScreenPowerStatusUpdates()
  }
}

service.configureHeartBeatInterval = () => {
  const interval = Logger.heartbeatInterval();

  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player) {
      try {
        Logger.viewerDebug( `Player set heartbeat interval ${ interval }` );

        player.setHeartbeatInterval( `${ interval }` );
      } catch( error ) {
        Logger.viewerError( "E999999999", "Player set heartbeat interval invocation failed", error.message );
      }
    }
  }
}

service.bindPlayerUpdateButton = () => {
  const button = document.querySelector("#player-update-modal button");
  const modal = document.querySelector("#player-update-modal");

  if (!button || !modal || typeof AndroidPlayer === 'undefined') {return}

  button.onclick = ()=>{
    modal.innerHTML = "Retrieving update, please wait."
    setTimeout(()=>AndroidPlayer.requestPlayerUpdate());
    button.onclick = ()=>{}
  };
}

service.scheduleLGPowerOn = (time) => {
  const player = service.getInjectedPlayer();

  if (player && player.schedulePowerOn && time) {
    try {
      Logger.viewerDebug("Scheduling LG power on at", time);
      player.schedulePowerOn(time);
    } catch (error) {
      Logger.viewerError("E999999999", "Schedule LG power on invocation failed", error.message);
    }
  }

  return Promise.resolve();
}

service.processContentJson = json => {
  if (!json) {return;}

  if (json.display) {
    if (typeof json.display.orientation === 'number') {
      service.setOrientation(json.display.orientation)
    }
    if (typeof json.display.autostartOnBoot === 'boolean') {
      service.setAutoStartOnBoot(json.display.autostartOnBoot)
    }
    if (typeof json.display.setAsScreenSaver === 'boolean' && typeof json.display.idleTimeBeforePlayerLaunch === 'number') {
      service.setScreenSaverConfiguration(json.display.setAsScreenSaver, json.display.idleTimeBeforePlayerLaunch)
    }
    if (typeof json.display.volumeControlSupported === 'boolean' && typeof json.display.volumePercentage === 'number') {
      service.configureVolumeControl(json.display.volumeControlSupported, json.display.volumePercentage)
    }
    if (typeof json.display.keyOperationModeDevice === 'string') {
      service.setLGDevicePhysicalButtonMode(json.display.keyOperationModeDevice)
    }

    const isRebootablePlayer = ConfigurationFactory.isBrightsignPlayer() || ConfigurationFactory.isGiadaRecommendedDevice() || ConfigurationFactory.isAvocorRSeries() || ConfigurationFactory.isAvocorKSeries() || ConfigurationFactory.isWebOSPlayer();

    const action = window.playerSupportsReboot || isRebootablePlayer ?
      service.requestReboot : service.requestRestart;

    if (ConfigurationFactory.isWebOSPlayer()) {
      if (json.display.restartEnabled && json.display.restartTime) {
        service.scheduleLGPowerOn(json.display.restartTime);
      }
    }

    // wait one minute before scheduling, so it doesn't reboot multiple times
    setTimeout(()=>{
      RebootScheduler.scheduleReboot(json, action);
    }, ONE_MINUTE);
  }

  return json;
}

service.setOrientation = rot => {
  // Apps/core set orientation as rotation from landscape (i.e. 0 is landscape, 90 is portrait)
  if (!Number.isInteger(rot)) {return;}

  if (ConfigurationFactory.isForcedRotationDevice()) {
    Logger.viewerDebug("Setting player orientation is problematic in some Android devices, skipping");
    return;
  }

  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.setScreenOrientation) {
      try {
        Logger.viewerDebug( `Setting orientation to ${rot}` );

        player.setScreenOrientation(rot);
      } catch( error ) {
        Logger.viewerError( "E999999999", "Player set orientation invocation failed", error.message );
      }
    }
  }
}

service.setAutoStartOnBoot = value => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.setAutoStartOnBoot) {
      try {
        Logger.viewerDebug( `Setting autoStart to ${value}` );

        player.setAutoStartOnBoot(value);
      } catch( error ) {
        Logger.viewerError( "E999999999", "Player set autoStartOnBoot invocation failed", error.message );
      }
    }
  }
}

service.requestReboot = () => {
  if (ConfigurationFactory.isBrightsignPlayer()) {
    Logger.viewerDebug(`Request BrightSign reboot` );

    window.postMessage({from: "viewer", msg: "reboot-request"}, location.origin);
    // For some reason "isWebPlayerBasedRisePlayer" is not working for Signage Stick, we need to investigate this later."
  } else if (ConfigurationFactory.isWebPlayerBasedRisePlayer() || ConfigurationFactory.isAmazonSignageStick()) {
    const player = service.getInjectedPlayer();

    if (player && player.requestReboot) {
      try {
        Logger.viewerDebug( `Request player reboot` );

        service.flushLocalStorageToDisk();
        player.requestReboot();
      } catch( error ) {
        Logger.viewerError( "E999999999", "Request player reboot invocation failed", error.message );
      }
    }
  } else if(ConfigurationFactory.isChromeOsPwaPlayerExtensionAvailable()) {
    Logger.viewerDebug( `Request Chrome OS PWA Player reboot` );

    ChromeOSPwaExtensionMessaging.requestReboot();
  }
}

service.requestRestart = (useEndpointLogs = true) => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    // When cancelling startup, a displayId will not be available and remote Logger would fail
    const logDebug = useEndpointLogs ? Logger.viewerDebug : console.log;
    const logError = useEndpointLogs ? Logger.viewerError : console.error;
    const player = service.getInjectedPlayer();

    if (player) {
      try {
        logDebug( `Request player restart` );

        service.flushLocalStorageToDisk();
        player.requestRestart();
      } catch( error ) {
        logError( "E999999999", "Request player restart invocation failed", error.message );
      }
    }
  } else if (ConfigurationFactory.isWebPlayer()) {
    Utils.reloadViewer();
  }
}

service.requestUpgrade = () => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.requestUpgrade) {
      try {
        Logger.viewerDebug( `Request player upgrade` );

        player.requestUpgrade();
      } catch( error ) {
        Logger.viewerError( "E999999999", "Request player upgrade invocation failed", error.message );
      }
    }
  }
}

service.skipRolloutPercentage = () => {
  const player = service.getInjectedPlayer();

  if (player && player.skipRolloutPercentage) {
    try {
      Logger.viewerDebug( `Requesting player to persist and skip rollout percentage` );

      player.skipRolloutPercentage();
    } catch( error ) {
      Logger.viewerError( "E999999999", "Request player skip rollout percentage invocation failed", error.message );
    }
  }
}

service.setDisplayId = displayId => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const logDebug = !!displayId ? Logger.viewerDebug : console.log;
    const logError = !!displayId ? Logger.viewerError : console.error;
    const player = service.getInjectedPlayer();

    if (player) {
      if (player.setDisplayIdAndMachineId) {
        try {
          const machineId = MachineId.id();
          logDebug( `Set display id: ${displayId} and machine id: ${machineId}` );

          player.setDisplayIdAndMachineId(displayId, machineId);
        } catch( error ) {
          logError( "E999999999", "Set player displayId and machineId invocation failed", error.message );
        }
      }
      else if (player.setDisplayId) {
        try {
          logDebug( `Set display id: ${displayId}` );

          player.setDisplayId(displayId);
        } catch( error ) {
          logError( "E999999999", "Set player displayId invocation failed", error.message );
        }
      }
    }
  } else if (ConfigurationFactory.isChromeOsPwaPlayerExtensionAvailable()) {
    Logger.viewerDebug( `Set Chrome OS PWA Player display id: ${displayId}` );

    ChromeOSPwaExtensionMessaging.setDisplayId(displayId);
  }
}

service.setCompanyId = companyId => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.setCompanyId) {
      try {
        Logger.viewerDebug( `Set company id: ${companyId}` );

        player.setCompanyId(companyId);
      } catch( error ) {
        Logger.viewerError( "E999999999", "Set player companyId invocation failed", error.message );
      }
    }
  }
}

service.loadDisplayIdFromPlayerIfAvailable = () => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.getDisplayId) {
      try {
        const displayId = player.getDisplayId();

        if (!displayId) {
          console.warn( "No display id found on player settings" );
          return;
        }

        console.log( `Loading display id from player: ${displayId}` );
        ConfigurationFactory.setDisplayId(displayId);
      } catch( error ) {
        console.error( "getDisplayId player invocation failed", error.message );
      }
    }
  } else if(ConfigurationFactory.isChromeOsPwaPlayerExtensionAvailable()) {
    const config = ConfigurationFactory.getChromeOSPwaConfiguration();
    const displayId = config.handoffDisplayId;

    console.log( `Loading display id from chrome handoff: ${displayId}` );

    if (!displayId) {
      console.warn( "No display id found on chrome handoff" );
      return;
    }

    ConfigurationFactory.setDisplayId(displayId);
  }
}

service.setScreenSaverConfiguration = (setAsScreenSaver, idleTimeBeforePlayerLaunch) => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.setScreenSaverConfiguration) {
      try {
        Logger.viewerDebug( "Set screensaver configuration", { setAsScreenSaver, idleTimeBeforePlayerLaunch } );

        player.setScreenSaverConfiguration(setAsScreenSaver, idleTimeBeforePlayerLaunch);
      } catch( error ) {
        Logger.viewerError( "E999999999", "Player setScreenSaverConfiguration invocation failed", error.message );
      }
    }
  }
}

service.configureVolumeControl = (volumeControlSupported, volumePercentage) => {
  if (ConfigurationFactory.viewerSupportsVolumeControl()) {
    return VolumeControlService.configure( volumeControlSupported, volumePercentage, service.getInjectedPlayer() );
  }

  return Promise.resolve();
}

service.notifyCurrentVolumeLevel = currentVolumeLevel => {
  if (ConfigurationFactory.viewerSupportsVolumeControl()) {
    VolumeControlService.notifyCurrentVolumeLevel( currentVolumeLevel );
  }
}

service.notifyCurrentVolumeLevelReadError = errorMessage => {
  if (ConfigurationFactory.viewerSupportsVolumeControl()) {
    VolumeControlService.notifyCurrentVolumeLevelReadError( errorMessage );
  }
}

service.setVolumeLevelToAlert = () => {
  if (ConfigurationFactory.viewerSupportsVolumeControl()) {
    return VolumeControlService.setVolumeLevelToAlert();
  }

  return Promise.resolve();
}

service.setVolumeLevelToDefault = () => {
  if (ConfigurationFactory.viewerSupportsVolumeControl()) {
    return VolumeControlService.setVolumeLevelToDefault();
  }

  return Promise.resolve();
}

service.setLGDevicePhysicalButtonMode = (mode) => {
  if (ConfigurationFactory.isWebOSPlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.setDevicePhysicalButtonsMode) {
      try {
        player.setDevicePhysicalButtonsMode(mode);
      } catch( error ) {
        Logger.viewerError( "E999999999", "Set physical buttons mode invocation failed", error.message );
      }
    }
  }

  return Promise.resolve();
}

service.sendScreenshotSaved = clientId => {
  ContentSentinelDispatcher.sendControllerMessage({
    msg: "screenshot-saved", clientId
  }, window);

  console.log('screenshot uploaded and screenshot-saved message sent', clientId);
}

service.sendScreenshotFailed = clientId => {
  ContentSentinelDispatcher.sendControllerMessage({
    msg: "screenshot-failed", clientId
  }, window);

  console.log('screenshot-failed message sent', clientId);
}

service.sendScreenPowerState = (state) => {
  ContentSentinelDispatcher.sendControllerMessage({
    msg: "screen-power-state", state
  }, window);

  console.log('screen-power-state message sent', state);
}

service.requestFirmwareUpgrade = data => {
  Logger.viewerDebug("Firmware upgrade request received", data);

  if (!ConfigurationFactory.isWebOSPlayer() && !ConfigurationFactory.isAmazonSignageStick()) {
    Logger.viewerError( "E999999999", "Firmware upgrade request not supported on this platform, only supported on WebOS players and Amazon Signage Sticks", JSON.stringify(data) );
    return;
  }

  const player = service.getInjectedPlayer();

  if (!player) {
    Logger.viewerError( "E999999999", "Firmware upgrade request not supported on this platform, player object is not available", JSON.stringify(data) );
    if (service.sendFirmwareUpgradeFailed) {
      service.sendFirmwareUpgradeFailed();
    } else {
      Logger.viewerError( "E999999999", "Firmware upgrade request failed but sendFirmwareUpgradeFailed callback is not available", JSON.stringify(data) );
    }
    return;
  }

  if (ConfigurationFactory.isAmazonSignageStick()) {
    if (player.requestOtaUpdate) {
      try {
        Logger.viewerDebug( `Request player OTA update` );

        player.requestOtaUpdate();
      } catch( error ) {
        Logger.viewerError( "E999999999", "Request player OTA update invocation failed", error.message );
      }
    }
  } else {
    if (!data || !data.url || !data.clientId) {
      return Logger.viewerError( "E999999999", "Invalid firmware upgrade request, url or clientId is missing", JSON.stringify(data) );
    }

    if (!player.requestFirmwareUpgrade) {
      Logger.viewerError( "E999999999", "Firmware upgrade request not supported on this platform, player does not support requestFirmwareUpgrade", JSON.stringify(data) );
      if (service.sendFirmwareUpgradeFailed) {
        service.sendFirmwareUpgradeFailed();
      } else {
        Logger.viewerError( "E999999999", "Firmware upgrade request failed but sendFirmwareUpgradeFailed callback is not available", JSON.stringify(data) );
      }
      return;
    }

    Logger.viewerDebug("Firmware upgrade request sent to player", data);

    player.requestFirmwareUpgrade(data.url, (resp) => {
      console.log("Upgrade CB", data.clientId, typeof resp, resp);
      Logger.viewerImportant("Firmware upgrade response received from player", resp);
    });
  }
}

service.requestScreenshot = data => {
  if (!data || !data.url || !data.clientId) {
    return Logger.viewerError( "E999999999", "Invalid screenshot request", JSON.stringify(data) );
  }

  if (ConfigurationFactory.isBrightsignPlayer()) {
    Logger.viewerDebug(`Request BrightSign screenshot` );

    window.postMessage({from: "viewer", msg: "screenshot-request", id: data.clientId, url: data.url}, location.origin);
  } else if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player) {
      try {
        Logger.viewerDebug( "Request player screenshot" );

        player.requestScreenshot(data.url, data.clientId);
      } catch( error ) {
        Logger.viewerError( "E999999999", "Request player screenshot invocation failed", error.message );

        service.sendScreenshotFailed( data.clientId );
      }
    } else {
      service.sendScreenshotFailed( data.clientId );
    }
  } else if(ConfigurationFactory.isChromeOsPwaPlayerExtensionAvailable()) {
    Logger.viewerDebug( "Request Chrome OS PWA Player screenshot" );

    ChromeOSPwaExtensionMessaging.requestScreenshot( data.url )
    .then( () => {
      service.sendScreenshotSaved( data.clientId );
    })
    .catch( error => {
      console.error( error );
      Logger.viewerError( "E999999999", "Request Chrome OS PWA Player screenshot failed",
        { error: error.message, clientId: data.clientId, url: data.url }
      );

      service.sendScreenshotFailed( data.clientId );
    });
  }
}

service.setDisplayControlConfiguration = configuration => {
  if (ConfigurationFactory.isChromeOsPwaPlayerExtensionAvailable()) {
    return DisplayControlHandler.configure(configuration);
  } else if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.setDisplayControlConfiguration) {
      try {
        DisplayControlConfigurationLogging.logConfiguration(configuration || '');

        player.setDisplayControlConfiguration(configuration);
      } catch( error ) {
        Logger.viewerError( "E999999999", "Set display control configuration invocation failed", error.message );
      }
    }
  }

  return Promise.resolve();
}

service.turnDisplayOn = () => {
  Logger.viewerDebug( "turning display on" );

  service.displayOff = false;

  if (ConfigurationFactory.isBrightsignPlayer()) {
    Logger.viewerDebug(`Request BrightSign display on` );

    window.postMessage({from: "viewer", msg: "display-on"}, location.origin);
  } else if (ConfigurationFactory.isChromeOsPwaPlayerExtensionAvailable()) {
    Logger.viewerDebug( "Request Chrome OS PWA Player display on" );

    DisplayControlHandler.turnDisplayOn();
  } else if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.turnDisplayOn) {
      try {
        player.turnDisplayOn();
      } catch( error ) {
        Logger.viewerError( "E999999999", "Turn display on invocation failed", error.message );
      }
    }
  }
}

service.turnDisplayOff = () => {
  Logger.viewerDebug( "turning display off" );

  service.displayOff = true;

  if (ConfigurationFactory.isBrightsignPlayer()) {
    Logger.viewerDebug(`Request BrightSign display off` );

    window.postMessage({from: "viewer", msg: "display-off"}, location.origin);
  } else if (ConfigurationFactory.isChromeOsPwaPlayerExtensionAvailable()) {
    Logger.viewerDebug( "Request Chrome OS PWA Player display off" );

    DisplayControlHandler.turnDisplayOff();
  } else if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.turnDisplayOff) {
      try {
        player.turnDisplayOff();
      } catch( error ) {
        Logger.viewerError( "E999999999", "Turn display off invocation failed", error.message );
      }
    }
  }
}

service.notifyOfRegularContent = () => {
  window.dispatchEvent(new Event("notifyOfRegularContent"));

  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.notifyOfRegularContent) {
      try {
        player.notifyOfRegularContent();
      } catch( error ) {
        Logger.viewerError( "E999999999", "Notify of Regular Content invocation failed", error.message );
      }
    }
  }
}

service.clearCache = () => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    RemoteCacheController.clearCache(service);
  }
}

service.playerMessageFn = (msg, data) => {
  if (data && data.from === "viewer") {return;}

  if (msg === "poweron-request") {service.turnDisplayOn()}
  if (msg === "poweroff-request") {service.turnDisplayOff()}
  if (msg === "reboot-request") {service.requestReboot()}
  if (msg === "restart-request") {service.requestRestart()}
  if (msg === "screenshot-request") {service.requestScreenshot(data)}
  if (msg === "firmware-upgrade-request") {service.requestFirmwareUpgrade(data)}
  if (msg === "upgrade-request") {service.requestUpgrade()}
  if (msg === "skip-rollout-percent") {service.skipRolloutPercentage()}
  if (msg === "cache-clear") {service.clearCache()}
};

service.flushLocalStorageToDisk = () => {
  const player = service.getInjectedPlayer();

  if (player && player.flushLocalStorageToDisk) {
    try {
      player.flushLocalStorageToDisk();
    } catch( error ) {
      Logger.viewerError( "E999999999", "Flush local storage to disk invocation failed", error.message );
    }
  }
};

service.exitPlayer = () => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.exitPlayer) {
      try {
        player.exitPlayer();
      } catch( error ) {
        const currentTime = Date.now();
        const oneHourInMs = 60 * ONE_MINUTE;

        if (!service.lastExitError ||
            error.message !== service.lastExitError ||
            (currentTime - service.lastExitErrorTimestamp) > oneHourInMs) {

          Logger.viewerError( "E999999999", "Exit player invocation failed", error.message );

          service.lastExitError = error.message;
          service.lastExitErrorTimestamp = currentTime;
        }
      }
    }
  }
};

service.supportsActivateFromUsb = () => {
  if (!ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    return false;
  }

  const player = service.getInjectedPlayer();

  return !!(player && player.activateFromUsb);
};

service.activateFromUsb = () => {
  if (ConfigurationFactory.isWebPlayerBasedRisePlayer()) {
    const player = service.getInjectedPlayer();

    if (player && player.activateFromUsb) {
      try {
        Logger.viewerDebug( "Activate from USB drive" );

        player.activateFromUsb();
      } catch( error ) {
        Logger.viewerError( "E999999999", "Activate from USB invocation failed", error.message );
      }
    }
  }
};

service.clean = () => {
  service.lastExitError = null;
  service.lastExitErrorTimestamp = null;
}

module.exports = service;
